import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGeri } from '@/lib/geri';
import api from '@/lib/api';
import { useProducts } from '@/contexts/ProductsContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ArrowLeft, Save, Plus, X, Package, FlaskConical, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import ProductCombobox from '@/components/ProductCombobox';
import SearchableSelect from '@/components/SearchableSelect';
import { trSayi, trPara } from '@/lib/sayi';

/**
 * Ürünün ana biriminde kaç eder? (Reçete ekranındaki `convertToMainUnit`in eşi.)
 *
 * Yalnızca EKRANDAKİ tahmini maliyet için: gerçek dönüşümü ve doğrulamayı
 * sunucu yapıyor (`unit_utils.to_main_qty`), burada bir sapma paraya yansımaz.
 */
const anaBirime = (urun, birim, miktar, birimler) => {
  const m = parseFloat(miktar);
  if (!urun || !m) return 0;
  const ana = birimler.find(u => u.id === urun.unit_id || u.abbreviation === urun.unit);
  const kaynak = birimler.find(u => u.abbreviation === birim);
  if (!ana || !kaynak || ana.id === kaynak.id) return m;
  if (ana.unit_group === kaynak.unit_group && ana.unit_group !== 'quantity') {
    return m * kaynak.to_base_factor / ana.to_base_factor;
  }
  for (const c of (urun.unit_conversions || [])) {
    const dan = birimler.find(u => u.id === c.from_unit_id);
    if (dan && dan.abbreviation === birim && c.factor) return m * c.factor;
  }
  return m;
};

const BOS_KALEM = { product_card_id: '', quantity: '', unit: '' };

export default function ProductionOrderNew() {
  const navigate = useNavigate();
  // Geri = önceki sayfa; önceki adım yoksa eski liste adresi (bkz. lib/geri.js)
  const geri = useGeri('/erp/production-orders');
  // Ayni sayfa hem yeni emir hem DUZENLEME: alanlar birebir ayni, iki kopya
  // tutmak bir gun ayrisirdi. `id` varsa duzenleme kipindeyiz.
  const { id } = useParams();
  const duzenleme = Boolean(id);
  const { products } = useProducts();
  const [recipes, setRecipes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [birimler, setBirimler] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  // REÇETELİ / REÇETESİZ. Reçetesizde girdiler ölçülmüş değer olarak
  // yazılır; mevsime göre değişen verimi (1 lt limon suyu yazın 5 kg kışın
  // 8 kg limon) sabit oranlı reçete anlatamıyor.
  const [kip, setKip] = useState('receteli');
  // Uretimin GERCEKLESTIGI gun; stok hareketleri buna yazilir.
  const [form, setForm] = useState({
    recipe_id: '', warehouse_id: '', planned_quantity: '1', notes: '',
    planned_date: new Date().toLocaleDateString('en-CA'),
    product_card_id: '', quantity_unit: '',
  });
  const [kalemler, setKalemler] = useState([{ ...BOS_KALEM }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/recipes').then(r => setRecipes(
      [...(r.data || [])].sort((a, b) =>
        (a.product_name || '').localeCompare(b.product_name || '', 'tr'))
    )).catch(() => {});
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
    api.get('/units').then(r => setBirimler(r.data)).catch(() => {});
  }, []);

  // Duzenlemede emri cek; tamamlanmis emir buraya HIC girmemeli (once geri alinir)
  useEffect(() => {
    if (!id) return;
    api.get(`/production-orders/${id}`).then(r => {
      const o = r.data;
      if (o.status === 'completed' || o.status === 'cancelled') {
        toast.error(o.status === 'completed'
          ? 'Tamamlanmış emir düzenlenemez — önce Geri Al ile stok hareketlerini iade edin'
          : 'İptal edilmiş emir düzenlenemez');
        navigate('/erp/production-orders');
        return;
      }
      setKip(o.recipe_id ? 'receteli' : 'recetesiz');
      setForm({
        recipe_id: o.recipe_id || '', warehouse_id: o.warehouse_id || '',
        planned_quantity: String(o.planned_quantity ?? '1'), notes: o.notes || '',
        planned_date: (o.planned_date || '').slice(0, 10)
          || new Date().toLocaleDateString('en-CA'),
        product_card_id: o.product_card_id || '', quantity_unit: o.quantity_unit || '',
      });
      if (!o.recipe_id) {
        setKalemler((o.ingredients_consumed || []).map(i => ({
          product_card_id: i.product_card_id || '',
          quantity: String(i.planned_qty ?? ''),
          unit: i.unit || '',
          // Geri alinmis emirde gerceklesen miktar korunuyor; ekran onu
          // degistirmiyor ama kaydederken de KAYBETMEMELI.
          actual_qty: i.actual_qty ?? null,
        })));
      }
      if (o.recipe_id) api.get(`/recipes/${o.recipe_id}`).then(x => setSelectedRecipe(x.data)).catch(() => {});
    }).catch(() => {
      toast.error('Üretim emri bulunamadı');
      navigate('/erp/production-orders');
    });
  }, [id, navigate]);

  const selectRecipe = async (rid) => {
    setForm(p => ({ ...p, recipe_id: rid }));
    try {
      const res = await api.get(`/recipes/${rid}`);
      setSelectedRecipe(res.data);
    } catch {
      const r = recipes.find(x => x.id === rid);
      setSelectedRecipe(r);
    }
  };

  // Cikti: mamul ve yari mamul. Girdi: DEMIRBAS disinda her sey (recete
  // ekraniyla ayni ayrim — orada dislama secildi ki yeni bir urun turu
  // eklendiginde kalem sessizce listeden dusmesin).
  const ciktiUrunleri = products.filter(p => ['finished', 'semi_finished'].includes(p.type));
  const girdiUrunleri = products.filter(p => p.type !== 'fixed_asset');
  const ciktiUrun = products.find(p => p.id === form.product_card_id);

  // Urunun ana birim grubundaki birimler + kartin kendi donusumleri
  const uygunBirimler = (urun) => {
    if (!urun) return birimler;
    const ana = birimler.find(u => u.id === urun.unit_id || u.abbreviation === urun.unit);
    if (!ana) return birimler;
    const liste = birimler.filter(u => u.unit_group === ana.unit_group);
    (urun.unit_conversions || []).forEach(c => {
      const alt = birimler.find(u => u.id === c.from_unit_id);
      if (alt && !liste.find(x => x.id === alt.id)) liste.push(alt);
    });
    return liste.length ? liste : birimler;
  };

  const kalemDegistir = (i, alan, deger) => {
    setKalemler(prev => prev.map((k, j) => {
      if (j !== i) return k;
      const yeni = { ...k, [alan]: deger };
      if (alan === 'product_card_id') {
        yeni.unit = products.find(p => p.id === deger)?.unit || '';
      }
      return yeni;
    }));
  };

  const kalemMaliyeti = (k) => {
    const p = products.find(x => x.id === k.product_card_id);
    if (!p?.last_purchase_price) return 0;
    return anaBirime(p, k.unit, k.quantity, birimler) * p.last_purchase_price;
  };
  const toplamMaliyet = kalemler.reduce((t, k) => t + kalemMaliyeti(k), 0);
  const uretilecek = parseFloat(form.planned_quantity) || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.warehouse_id) { toast.error('Depo seçin'); return; }

    let govde;
    if (kip === 'receteli') {
      if (!form.recipe_id) { toast.error('Reçete seçin'); return; }
      govde = {
        recipe_id: form.recipe_id, warehouse_id: form.warehouse_id,
        planned_quantity: parseFloat(form.planned_quantity),
        planned_date: form.planned_date, notes: form.notes,
      };
    } else {
      if (!form.product_card_id) { toast.error('Üretilen ürünü seçin'); return; }
      const dolu = kalemler.filter(k => k.product_card_id && parseFloat(k.quantity) > 0);
      if (!dolu.length) { toast.error('En az bir girdi ürünü ekleyin'); return; }
      govde = {
        // Sunucu `recipe_id` YOKSA recetesiz sayar; duzenlemede kipi
        // degistirmek icin de bu alan acikca null gitmeli.
        recipe_id: null, warehouse_id: form.warehouse_id,
        product_card_id: form.product_card_id,
        quantity_unit: form.quantity_unit || ciktiUrun?.unit || 'adet',
        planned_quantity: parseFloat(form.planned_quantity),
        planned_date: form.planned_date, notes: form.notes,
        ingredients: dolu.map(k => ({
          product_card_id: k.product_card_id,
          planned_qty: parseFloat(k.quantity),
          unit: k.unit || products.find(p => p.id === k.product_card_id)?.unit || 'adet',
          actual_qty: k.actual_qty ?? null,
        })),
      };
    }

    setLoading(true);
    try {
      if (duzenleme) await api.patch(`/production-orders/${id}`, govde);
      else await api.post('/production-orders', govde);
      toast.success(duzenleme ? 'Üretim emri güncellendi' : 'Uretim emri olusturuldu');
      navigate('/erp/production-orders');
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata olustu'); }
    setLoading(false);
  };

  return (
    <div data-testid="production-order-new-page">
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" onClick={geri} className="text-[#8a7e70] hover:text-[#1a1916]" data-testid="back-btn">
          <ArrowLeft size={18} />
        </Button>
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">
          {duzenleme ? 'Üretim Emrini Düzenle' : 'Yeni Uretim Emri'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* KİP SEÇİMİ. Reçetesiz üretim ayrı bir ekran değil aynı emrin öbür
            hali: tamamlama, stok, maliyet ve geri alma birebir aynı yürüyor.
            Ayrı ekran yapmak o akışın ikinci bir kopyasını doğururdu. */}
        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { deger: 'receteli', ad: 'Reçeteli Üretim', ikon: BookOpen,
                aciklama: 'Malzemeler reçeteden ve miktardan hesaplanır.' },
              { deger: 'recetesiz', ad: 'Reçetesiz Üretim', ikon: FlaskConical,
                aciklama: 'Girdileri ve çıktıyı kendiniz yazarsınız — verim her seferinde farklıysa.' },
            ].map(({ deger, ad, ikon: Ikon, aciklama }) => (
              <button key={deger} type="button" onClick={() => setKip(deger)}
                data-testid={`uretim-kip-${deger}`}
                className={`text-left rounded-lg border p-3 transition-colors ${
                  kip === deger
                    ? 'border-[#2e2d29] bg-[#e8dfd0]'
                    : 'border-[#d0c4b0] bg-white hover:border-[#b0a090]'}`}>
                <span className="flex items-center gap-2 font-semibold text-[#1a1916]">
                  <Ikon size={16} className="shrink-0" /> {ad}
                </span>
                <span className="block text-xs text-[#8a7e70] mt-1">{aciklama}</span>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Tuketim ile karistirilmasin: orada CIKTI YOK, mal yalnizca stoktan
            duser (personel yemegi, deneme). Burada bir urun URETILIYOR. */}
        {!duzenleme && kip === 'recetesiz' && (
          <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#2e6fa0]/30
                          bg-[#2e6fa0]/5 p-3">
            <Package size={18} className="text-[#2e6fa0] shrink-0" />
            <p className="text-sm text-[#3F3E39] flex-1 min-w-[220px]">
              Ortaya bir ürün <strong>çıkmıyorsa</strong> (personel yemeği, deneme, demo)
              {' '}bu ekran değil <strong>Reçetesiz Tüketim</strong> kullanılmalı.
            </p>
            <Button type="button" variant="outline"
              onClick={() => navigate('/erp/consumptions/new')}
              className="border-[#2e6fa0] text-[#2e6fa0] hover:bg-[#2e6fa0]/10 font-semibold"
              data-testid="recetesiz-tuketim-btn">
              Reçetesiz Tüketim
            </Button>
          </div>
        )}

        <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
          <CardHeader className="border-b border-[#d0c4b0]">
            <CardTitle className="text-lg font-bold text-[#1a1916]">Emir Bilgileri</CardTitle>
          </CardHeader>
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {kip === 'receteli' ? (
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Reçete *</Label>
                {/* ARANABİLİR (kullanıcı, 2026-09-27): 170 aktif reçete var,
                    düz açılır listede gözle bulmak zordu. Arama ÜRÜN KODUNU da
                    kapsıyor — kod `hint` alanında ve `SearchableSelect` hem
                    etiketi hem ipucunu tarıyor. */}
                <SearchableSelect
                  value={form.recipe_id}
                  options={recipes.map(r => ({
                    value: r.id,
                    label: r.product_name,
                    hint: [r.product_code, `v${r.version}`,
                           `${trSayi(r.yield_quantity)} ${r.yield_unit || ''}`.trim()]
                      .filter(Boolean).join(' · '),
                  }))}
                  onChange={selectRecipe}
                  placeholder="Reçete seçin..."
                  searchPlaceholder="Reçete adı ya da ürün kodu..."
                  emptyText="Reçete bulunamadı"
                  azamiSonuc={300}
                  testId="production-recipe-select" />
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-[#3F3E39] font-medium">Üretilen Ürün *</Label>
                <ProductCombobox
                  value={form.product_card_id}
                  products={ciktiUrunleri}
                  placeholder="Mamul / yarı mamul seçin..."
                  emptyText="Ürün bulunamadı"
                  testId="production-output-product"
                  onChange={(v) => setForm(p => ({
                    ...p, product_card_id: v,
                    quantity_unit: products.find(x => x.id === v)?.unit || '',
                  }))}
                />
                <p className="text-xs text-[#8a7e70]">
                  Çıktı, kayıtlı ürün kartlarından seçilir — stoğa o kartın altına girer.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Üretim Tarihi *</Label>
              <Input type="date" value={form.planned_date}
                onChange={(e) => setForm(p => ({ ...p, planned_date: e.target.value }))}
                max={new Date().toLocaleDateString('en-CA')}
                className="bg-white border-[#d0c4b0]" data-testid="production-date" />
              <p className="text-xs text-[#8a7e70]">
                Stok hareketleri bu tarihe işlenir. Varsayılan bugündür.
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Depo *</Label>
              <Select value={form.warehouse_id} onValueChange={(v) => setForm(p => ({ ...p, warehouse_id: v }))}>
                <SelectTrigger className="bg-white border-[#d0c4b0]" data-testid="production-warehouse-select">
                  <SelectValue placeholder="Depo secin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">
                Uretilecek Miktar *
                {kip === 'receteli' && selectedRecipe && (
                  <span className="text-[#8a7e70] font-normal"> ({selectedRecipe.yield_unit})</span>
                )}
              </Label>
              <div className="flex gap-2">
                {/* step="any": olculen deger; sabit adim tarayicida "adim uyusmazligi" yapip formu sessizce engelliyor */}
                <Input type="number" step="any" value={form.planned_quantity}
                  onChange={(e) => setForm(p => ({ ...p, planned_quantity: e.target.value }))}
                  className="bg-white border-[#d0c4b0]" data-testid="production-quantity-input" />
                {kip === 'recetesiz' && (
                  <Select value={form.quantity_unit}
                    onValueChange={(v) => setForm(p => ({ ...p, quantity_unit: v }))}>
                    <SelectTrigger className="bg-white border-[#d0c4b0] w-28" data-testid="production-quantity-unit">
                      <SelectValue placeholder="Birim" />
                    </SelectTrigger>
                    <SelectContent>
                      {uygunBirimler(ciktiUrun).map(u => (
                        <SelectItem key={u.id} value={u.abbreviation}>{u.abbreviation}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {kip === 'receteli' && selectedRecipe && (
                <p className="text-xs text-[#8a7e70]">
                  Recete bir kerede {selectedRecipe.yield_quantity} {selectedRecipe.yield_unit} uretiyor.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-[#3F3E39] font-medium">Notlar</Label>
              <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))}
                className="bg-white border-[#d0c4b0]" data-testid="production-notes-input" />
              {kip === 'recetesiz' && (
                <p className="text-xs text-[#8a7e70]">
                  Verimin neden saptığını buraya yazın (mevsim, partinin kalitesi…).
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {kip === 'receteli' && selectedRecipe && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
            <CardHeader className="border-b border-[#d0c4b0]">
              <CardTitle className="text-lg font-bold text-[#1a1916]">Gerekli Malzemeler (Tahmini)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Malzeme</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right">Gerekli Miktar</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Birim</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedRecipe.ingredients?.map((ing, i) => {
                    const multiplier = (parseFloat(form.planned_quantity) || 1) / (selectedRecipe.yield_quantity || 1);
                    return (
                      <TableRow key={i} className="border-[#d0c4b0]">
                        <TableCell className="text-[#1a1916]">{ing.product_name || ing.product_card_id}</TableCell>
                        {/* trSayi: `toFixed(3)` ondaligi NOKTA yaziyordu ve
                            "1.500" 1.500 BIRIM sanilliyordu (kullanici,
                            2026-09-22). Sondaki sifirlar da atiliyor. */}
                        <TableCell className="text-right font-semibold text-[#1a1916]">
                          {trSayi(ing.quantity * multiplier)}
                        </TableCell>
                        <TableCell className="text-[#8a7e70]">{ing.unit}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {kip === 'recetesiz' && (
          <Card className="bg-[#faf7f2] border-[#d0c4b0] shadow-sm">
            <CardHeader className="border-b border-[#d0c4b0] flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-bold text-[#1a1916]">Kullanılan Girdiler</CardTitle>
                <p className="text-xs text-[#8a7e70] mt-1">
                  Bu partide fiilen ne kullanıldıysa o yazılır. Miktarı değiştirmek
                  girdileri oranlamaz — ölçtüğünüz değerler korunur.
                </p>
              </div>
              <Button type="button" variant="outline" size="sm"
                onClick={() => setKalemler(p => [...p, { ...BOS_KALEM }])}
                className="border-[#d0c4b0]" data-testid="girdi-ekle-btn">
                <Plus size={14} className="mr-1" /> Girdi Ekle
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ürün</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-32">Miktar</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase w-28">Birim</TableHead>
                    <TableHead className="text-[#1a1916] font-bold text-xs uppercase text-right w-32">Tahmini Maliyet</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {kalemler.map((k, i) => {
                    const urun = products.find(p => p.id === k.product_card_id);
                    return (
                      <TableRow key={i} className="border-[#d0c4b0]">
                        <TableCell>
                          <ProductCombobox
                            value={k.product_card_id}
                            products={girdiUrunleri}
                            placeholder="Ürün seçin veya arayın..."
                            emptyText="Ürün bulunamadı"
                            className="h-9 text-sm"
                            testId={`girdi-urun-${i}`}
                            onChange={(v) => kalemDegistir(i, 'product_card_id', v)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input type="number" step="any" value={k.quantity}
                            onChange={(e) => kalemDegistir(i, 'quantity', e.target.value)}
                            className="bg-white border-[#d0c4b0]" data-testid={`girdi-miktar-${i}`} />
                        </TableCell>
                        <TableCell>
                          <Select value={k.unit} onValueChange={(v) => kalemDegistir(i, 'unit', v)}>
                            <SelectTrigger className="bg-white border-[#d0c4b0] w-24" data-testid={`girdi-birim-${i}`}>
                              <SelectValue placeholder="-" />
                            </SelectTrigger>
                            <SelectContent>
                              {uygunBirimler(urun).map(u => (
                                <SelectItem key={u.id} value={u.abbreviation}>{u.abbreviation}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right font-mono text-sm text-[#3F3E39]">
                          {trPara(kalemMaliyeti(k))} TL
                        </TableCell>
                        <TableCell>
                          <Button type="button" variant="ghost" size="icon"
                            onClick={() => setKalemler(p => p.filter((_, j) => j !== i))}
                            className="text-[#7a3535] hover:bg-[#7a3535]/10" data-testid={`girdi-sil-${i}`}>
                            <X size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {kalemler.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-[#8a7e70]">
                        "Girdi Ekle" ile kullandığınız ürünleri yazın
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              {/* Tahmini: gercek maliyet TAMAMLAMADA, girilen gerceklesen
                  miktarlarla hesaplanir (sunucu). Burasi yalnizca fikir verir. */}
              <div className="flex flex-wrap justify-end gap-6 border-t border-[#d0c4b0] px-6 py-3 text-sm">
                <span className="text-[#8a7e70]">
                  Tahmini toplam:{' '}
                  <strong className="text-[#1a1916] font-mono">{trPara(toplamMaliyet)} TL</strong>
                </span>
                {uretilecek > 0 && (
                  <span className="text-[#8a7e70]">
                    Birim maliyet:{' '}
                    <strong className="text-[#1a1916] font-mono">
                      {trPara(toplamMaliyet / uretilecek)} TL
                    </strong>
                    {' '}/ {form.quantity_unit || ciktiUrun?.unit || 'birim'}
                  </span>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={geri} className="border-[#d0c4b0]" data-testid="cancel-btn">Iptal</Button>
          <Button type="submit" disabled={loading} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="save-production-btn">
            <Save size={16} className="mr-2" />
            {loading ? 'Kaydediliyor...' : duzenleme ? 'Güncelle' : 'Olustur'}
          </Button>
        </div>
      </form>
    </div>
  );
}
